import axios from 'axios';

import createUrlWithParams from 'helpers/createUrlWithParams';

import METHOD from '../_methods';

import {
  parseActionTemplateDetailForFE,
  parseAllActionTemplatesForFE,
  parseAllActionTemplateHistoryForFE,
  parseAvailableAssetsResponseForFE,
  parseChoicesResponseForFE,
  parseAreasResponseForFE,
  parseRecommendedAssetsResponseForFE,
} from './actionLibrary.parsers';
import type { ActionTemplateAPI, RecommendedAssetsParams } from './actionLibrary.types';

const endpoint = 'api/v1/action-templates/';

// Legacy endpoints (for usePagination hook)
const actionTemplatesEndpoints = {
  getAll: (params?: { limit?: number; offset?: number; search?: string; ordering?: string; [key: string]: any }) => ({
    url: createUrlWithParams(endpoint, params || {}),
    method: METHOD.GET,
  }),
  get: (id: number) => ({
    url: `${endpoint}${id}/`,
    method: METHOD.GET,
  }),
  create: () => ({
    url: endpoint,
    method: METHOD.POST,
  }),
  update: (id: number) => ({
    url: `${endpoint}${id}/`,
    method: METHOD.PATCH,
  }),
  delete: (id: number) => ({
    url: `${endpoint}${id}/delete/`,
    method: METHOD.POST,
  }),
  restore: (id: number) => ({
    url: `${endpoint}${id}/restore/`,
    method: METHOD.POST,
  }),
  history: (id: number, params?: { limit?: number; offset?: number; ordering?: string; [key: string]: any }) => ({
    url: createUrlWithParams(`${endpoint}${id}/history/`, params || {}),
    method: METHOD.GET,
  }),
  getAvailableAssets: (params?: { search?: string }) => ({
    url: createUrlWithParams(`${endpoint}available_assets/`, params || {}),
    method: METHOD.GET,
  }),
  getRecommendedAssets: (id: number, params?: RecommendedAssetsParams) => ({
    url: createUrlWithParams(`${endpoint}${id}/recommended-assets/`, params || {}),
    method: METHOD.GET,
  }),
};

export const actionTemplatesApi: ActionTemplateAPI = {
  getAllActionTemplates: async params => {
    const { data } = await axios.get(createUrlWithParams(endpoint, params || {}));

    // Handle case where backend returns empty array directly
    if (Array.isArray(data)) {
      return {
        data: {
          count: data.length,
          next: null,
          previous: null,
          results: parseAllActionTemplatesForFE(data),
        },
      };
    }

    // Handle standard paginated response
    return {
      data: {
        count: data.count || 0,
        next: data.next || null,
        previous: data.previous || null,
        results: parseAllActionTemplatesForFE(data.results || []),
      },
    };
  },

  getSingleActionTemplate: async id => {
    const { data } = await axios.get(`${endpoint}${id}/`);
    return { data: parseActionTemplateDetailForFE(data) };
  },

  createActionTemplate: async data => {
    const { data: responseData } = await axios.post(endpoint, data);
    return { data: parseActionTemplateDetailForFE(responseData) };
  },

  createDraftActionTemplate: async (id, data) => {
    const { data: responseData } = await axios.post(`${endpoint}${id}/draft/`, data);
    return { data: parseActionTemplateDetailForFE(responseData) };
  },

  lockActionTemplate: async id => {
    const { data: responseData } = await axios.post(`${endpoint}${id}/lock/`);
    return { data: parseActionTemplateDetailForFE(responseData) };
  },

  publishActionTemplate: async (id, data) => {
    const { data: responseData } = await axios.post(`${endpoint}${id}/publish/`, data);
    return { data: parseActionTemplateDetailForFE(responseData) };
  },

  updateActionTemplate: async (id, data) => {
    const { data: responseData } = await axios.patch(`${endpoint}${id}/`, data);
    return { data: parseActionTemplateDetailForFE(responseData) };
  },

  deleteActionTemplate: async id => {
    const { data } = await axios.post(`${endpoint}${id}/delete/`, {});
    return { data: parseActionTemplateDetailForFE(data) };
  },

  restoreActionTemplate: async id => {
    const { data } = await axios.post(`${endpoint}${id}/restore/`, {});
    return { data: parseActionTemplateDetailForFE(data) };
  },

  getActionTemplateHistory: async (id, params) => {
    const { data } = await axios.get(createUrlWithParams(`${endpoint}${id}/history/`, params || {}));
    return {
      data: {
        count: data.count,
        next: data.next,
        previous: data.previous,
        results: parseAllActionTemplateHistoryForFE(data.results),
      },
    };
  },

  getAreas: async () => {
    const { data } = await axios.get(`${endpoint}areas/`);
    return { data: parseAreasResponseForFE(data) };
  },

  getAvailableAssets: async () => {
    const { data } = await axios.get(`${endpoint}available_assets/`);
    return { data: parseAvailableAssetsResponseForFE(data) };
  },

  getChoices: async () => {
    const { data } = await axios.get(`${endpoint}choices/`);
    return { data: parseChoicesResponseForFE(data) };
  },

  getSubareas: async area => {
    const { data } = await axios.get(createUrlWithParams(`${endpoint}subareas/`, { area }));
    return { data };
  },

  getRecommendedAssets: async (id, params) => {
    const { data } = await axios.get(createUrlWithParams(`${endpoint}${id}/recommended-assets/`, params || {}));

    // Handle both paginated response and direct array response
    const isPaginated = data && typeof data === 'object' && !Array.isArray(data) && 'results' in data;

    let results = [];
    if (isPaginated) {
      results = data.results || [];
    } else if (Array.isArray(data)) {
      results = data;
    }

    const count = isPaginated ? data.count || results.length : results.length;
    const next = isPaginated ? data.next || null : null;
    const previous = isPaginated ? data.previous || null : null;

    return {
      data: parseRecommendedAssetsResponseForFE({
        count,
        next,
        previous,
        results,
      }),
    };
  },
};

export default actionTemplatesEndpoints;
